import { AIPageContext, AIPageParamsMap, AIPageSlug } from '@euler/model/ai';
import { useFocusEffect } from '@react-navigation/native';
import { useCallback } from 'react';
import { useAIAssistant } from './AIAssistantProvider';

/**
 * Mark this screen as AI-enabled, pushing its page context and showing
 * the FAB while the screen is focused.
 *
 * **Call this only at the route/page/screen level** — never in a child
 * component. The hook uses `useFocusEffect` so it cooperates with
 * nested navigators (tabs inside stacks inside modals); the context is
 * pushed on focus and released on blur.
 *
 * Pages that don't call this hook get no FAB and no page context.
 * There is no global allow-list — opt-in is explicit per screen.
 *
 * `pageSlug` must be a member of the predefined `AIPageSlug` catalog
 * in `@euler/model/ai`; the ai-gateway rejects unknown slugs with 400.
 * The params type is enforced per slug at compile time — e.g.
 * `useAIAssistantForPage('task_detail', { taskNo })` is required, and
 * `useAIAssistantForPage('task_detail')` is a type error.
 *
 * The reference-equality guard on cleanup matters: in react-navigation,
 * a sibling's focus event fires BEFORE our blur, so by the time we run
 * cleanup the current context may already belong to the next screen.
 * Clearing unconditionally would wipe the new screen's claim.
 */
export function useAIAssistantForPage<K extends AIPageSlug>(
  ...args: AIPageParamsMap[K] extends undefined
    ? [pageSlug: K]
    : [pageSlug: K, requiredPageContext: AIPageParamsMap[K]]
) {
  const [pageSlug, requiredPageContext] = args as [
    K,
    AIPageParamsMap[K] | undefined,
  ];
  const { pageContext$ } = useAIAssistant();
  const paramsKey = JSON.stringify(requiredPageContext ?? {});

  useFocusEffect(
    useCallback(() => {
      const ctx = {
        page: pageSlug,
        ...(requiredPageContext != null ? { params: requiredPageContext } : {}),
      } as AIPageContext;
      pageContext$.next(ctx);

      return () => {
        if (pageContext$.getValue() === ctx) {
          pageContext$.next(undefined);
        }
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageSlug, paramsKey, pageContext$]),
  );
}
